/**
 * DbLoading - 加载效果组件
 *
 * 特性：
 * - 多种动画类型：spinner, dots, pulse, wave, bounce, circular
 * - 支持全屏遮罩模式
 * - 支持命令式调用（DbLoading.show / DbLoading.hide）
 * - 可自定义颜色、尺寸、文字、动画速度
 */

import React, { useEffect, useRef, useCallback } from 'react';
import {
    View,
    Text,
    Modal,
    Animated,
    TouchableWithoutFeedback,
    Easing,
} from 'react-native';
import {
    DbLoadingProps,
    LoadingMethods,
    LoadingIndicatorProps,
    LoadingSize,
} from './DbLoading.types';
import {
    styles,
    LOADING_CONSTANTS,
    getNumericSize,
} from './DbLoading.styles';

// ==================== Loading 管理器（全局状态） ====================

type LoadingState = DbLoadingProps & { visible: boolean };
type LoadingListener = (state: LoadingState) => void;

class LoadingManager {
    private listeners: Set<LoadingListener> = new Set();
    private state: LoadingState = { visible: false };

    subscribe(listener: LoadingListener) {
        this.listeners.add(listener);
        return () => this.listeners.delete(listener);
    }

    private notify() {
        this.listeners.forEach(listener => listener(this.state));
    }

    show(options?: Partial<DbLoadingProps> | string) {
        const opts = typeof options === 'string' ? { text: options } : options || {};
        this.state = { ...opts, visible: true, fullscreen: true };
        this.notify();
    }

    hide() {
        this.state = { ...this.state, visible: false };
        this.notify();
    }

    getState() {
        return this.state;
    }
}

const loadingManager = new LoadingManager();

// ==================== 动画指示器组件 ====================

/** Spinner - 旋转圆环 */
const SpinnerIndicator: React.FC<{ size: number; color: string; secondaryColor?: string; duration?: number }> = ({
    size,
    color,
    secondaryColor,
    duration = 1000,
}) => {
    const spinValue = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const animation = Animated.loop(
            Animated.timing(spinValue, {
                toValue: 1,
                duration,
                easing: Easing.linear,
                useNativeDriver: true,
            })
        );
        animation.start();
        return () => animation.stop();
    }, [spinValue, duration]);

    const spin = spinValue.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
    });

    return (
        <Animated.View
            style={[
                styles.spinner,
                {
                    width: size,
                    height: size,
                    borderColor: secondaryColor || `${color}33`,
                    borderTopColor: color,
                    borderWidth: Math.max(2, size / 12),
                    transform: [{ rotate: spin }],
                },
            ]}
        />
    );
};

/** Dots - 三个跳动的点 */
const DotsIndicator: React.FC<{ size: number; color: string; duration?: number }> = ({
    size,
    color,
    duration = 600,
}) => {
    const dotSize = size / 4;
    const anims = [
        useRef(new Animated.Value(0)).current,
        useRef(new Animated.Value(0)).current,
        useRef(new Animated.Value(0)).current,
    ];

    useEffect(() => {
        const animations = anims.map((anim, index) =>
            Animated.loop(
                Animated.sequence([
                    Animated.delay(index * (duration / 3)),
                    Animated.timing(anim, {
                        toValue: 1,
                        duration: duration / 2,
                        easing: Easing.ease,
                        useNativeDriver: true,
                    }),
                    Animated.timing(anim, {
                        toValue: 0,
                        duration: duration / 2,
                        easing: Easing.ease,
                        useNativeDriver: true,
                    }),
                ])
            )
        );
        animations.forEach(a => a.start());
        return () => animations.forEach(a => a.stop());
    }, [anims, duration]);

    return (
        <View style={[styles.dotsContainer, { height: size }]}>
            {anims.map((anim, index) => (
                <Animated.View
                    key={index}
                    style={[
                        styles.dot,
                        {
                            width: dotSize,
                            height: dotSize,
                            backgroundColor: color,
                            transform: [
                                {
                                    scale: anim.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [0.6, 1],
                                    }),
                                },
                            ],
                            opacity: anim.interpolate({
                                inputRange: [0, 1],
                                outputRange: [0.4, 1],
                            }),
                        },
                    ]}
                />
            ))}
        </View>
    );
};

/** Pulse - 脉冲圆圈 */
const PulseIndicator: React.FC<{ size: number; color: string; duration?: number }> = ({
    size,
    color,
    duration = 1500,
}) => {
    const scaleAnim = useRef(new Animated.Value(0)).current;
    const opacityAnim = useRef(new Animated.Value(1)).current;

    useEffect(() => {
        const animation = Animated.loop(
            Animated.parallel([
                Animated.timing(scaleAnim, {
                    toValue: 1,
                    duration,
                    easing: Easing.out(Easing.ease),
                    useNativeDriver: true,
                }),
                Animated.timing(opacityAnim, {
                    toValue: 0,
                    duration,
                    easing: Easing.out(Easing.ease),
                    useNativeDriver: true,
                }),
            ])
        );
        animation.start();
        return () => animation.stop();
    }, [scaleAnim, opacityAnim, duration]);

    return (
        <View style={[styles.pulseContainer, { width: size, height: size }]}>
            <Animated.View
                style={[
                    styles.pulseCircle,
                    {
                        width: size,
                        height: size,
                        backgroundColor: color,
                        opacity: opacityAnim,
                        transform: [{ scale: scaleAnim }],
                    },
                ]}
            />
            <View
                style={{
                    width: size * 0.4,
                    height: size * 0.4,
                    borderRadius: size * 0.2,
                    backgroundColor: color,
                }}
            />
        </View>
    );
};

/** Wave - 波浪条 */
const WaveIndicator: React.FC<{ size: number; color: string; duration?: number }> = ({
    size,
    color,
    duration = 1200,
}) => {
    const barCount = 5;
    const barWidth = size / 8;
    const anims = Array.from({ length: barCount }, () => useRef(new Animated.Value(0.3)).current);

    useEffect(() => {
        const animations = anims.map((anim, index) =>
            Animated.loop(
                Animated.sequence([
                    Animated.delay(index * (duration / barCount / 2)),
                    Animated.timing(anim, {
                        toValue: 1,
                        duration: duration / 2,
                        easing: Easing.ease,
                        useNativeDriver: true,
                    }),
                    Animated.timing(anim, {
                        toValue: 0.3,
                        duration: duration / 2,
                        easing: Easing.ease,
                        useNativeDriver: true,
                    }),
                ])
            )
        );
        animations.forEach(a => a.start());
        return () => animations.forEach(a => a.stop());
    }, [anims, duration]);

    return (
        <View style={[styles.waveContainer, { height: size }]}>
            {anims.map((anim, index) => (
                <Animated.View
                    key={index}
                    style={[
                        styles.waveBar,
                        {
                            width: barWidth,
                            height: size,
                            backgroundColor: color,
                            transform: [{ scaleY: anim }],
                        },
                    ]}
                />
            ))}
        </View>
    );
};

/** Bounce - 弹跳球 */
const BounceIndicator: React.FC<{ size: number; color: string; duration?: number }> = ({
    size,
    color,
    duration = 600,
}) => {
    const ballSize = size / 3;
    const anims = [
        useRef(new Animated.Value(0)).current,
        useRef(new Animated.Value(0)).current,
        useRef(new Animated.Value(0)).current,
    ];

    useEffect(() => {
        const animations = anims.map((anim, index) =>
            Animated.loop(
                Animated.sequence([
                    Animated.delay(index * (duration / 3)),
                    Animated.timing(anim, {
                        toValue: 1,
                        duration: duration / 2,
                        easing: Easing.out(Easing.ease),
                        useNativeDriver: true,
                    }),
                    Animated.timing(anim, {
                        toValue: 0,
                        duration: duration / 2,
                        easing: Easing.in(Easing.ease),
                        useNativeDriver: true,
                    }),
                ])
            )
        );
        animations.forEach(a => a.start());
        return () => animations.forEach(a => a.stop());
    }, [anims, duration]);

    return (
        <View style={[styles.bounceContainer, { height: size }]}>
            {anims.map((anim, index) => (
                <Animated.View
                    key={index}
                    style={[
                        styles.bounceBall,
                        {
                            width: ballSize,
                            height: ballSize,
                            backgroundColor: color,
                            transform: [
                                {
                                    translateY: anim.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: [0, -size * 0.5],
                                    }),
                                },
                            ],
                        },
                    ]}
                />
            ))}
        </View>
    );
};

/** Circular - 圆形进度 */
const CircularIndicator: React.FC<{ size: number; color: string; secondaryColor?: string; duration?: number }> = ({
    size,
    color,
    secondaryColor,
    duration = 1500,
}) => {
    const rotateAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        const animation = Animated.loop(
            Animated.timing(rotateAnim, {
                toValue: 1,
                duration,
                easing: Easing.linear,
                useNativeDriver: true,
            })
        );
        animation.start();
        return () => animation.stop();
    }, [rotateAnim, duration]);

    const rotate = rotateAnim.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '360deg'],
    });

    const strokeWidth = Math.max(2, size / 10);

    return (
        <View style={[styles.circularContainer, { width: size, height: size }]}>
            {/* 背景圆环 */}
            <View
                style={{
                    width: size,
                    height: size,
                    borderRadius: size / 2,
                    borderWidth: strokeWidth,
                    borderColor: secondaryColor || `${color}33`,
                    position: 'absolute',
                }}
            />
            {/* 动画圆弧 */}
            <Animated.View
                style={{
                    width: size,
                    height: size,
                    borderRadius: size / 2,
                    borderWidth: strokeWidth,
                    borderColor: 'transparent',
                    borderTopColor: color,
                    borderRightColor: color,
                    transform: [{ rotate }],
                }}
            />
        </View>
    );
};

/** Loading 指示器选择器 */
const LoadingIndicator: React.FC<LoadingIndicatorProps> = ({
    type,
    size,
    color,
    secondaryColor,
    duration,
    children,
}) => {
    if (children) {
        return <>{children}</>;
    }

    switch (type) {
        case 'spinner':
            return <SpinnerIndicator size={size} color={color} secondaryColor={secondaryColor} duration={duration} />;
        case 'dots':
            return <DotsIndicator size={size} color={color} duration={duration} />;
        case 'pulse':
            return <PulseIndicator size={size} color={color} duration={duration} />;
        case 'wave':
            return <WaveIndicator size={size} color={color} duration={duration} />;
        case 'bounce':
            return <BounceIndicator size={size} color={color} duration={duration} />;
        case 'circular':
            return <CircularIndicator size={size} color={color} secondaryColor={secondaryColor} duration={duration} />;
        case 'custom':
            return null;
        default:
            return <SpinnerIndicator size={size} color={color} secondaryColor={secondaryColor} duration={duration} />;
    }
};

// ==================== DbLoading 组件 ====================

export const DbLoading: React.FC<DbLoadingProps> & LoadingMethods = ({
    visible = true,
    type = 'spinner',
    size = 'medium',
    color = LOADING_CONSTANTS.defaultColor,
    secondaryColor,
    text,
    textPosition = 'bottom',
    textStyle,
    textGap = LOADING_CONSTANTS.textGap,
    fullscreen = false,
    maskColor = LOADING_CONSTANTS.maskColor,
    maskClosable = false,
    onClose,
    style,
    contentStyle,
    duration,
    vertical = true,
    children,
    backgroundColor,
    borderRadius,
}) => {
    const numericSize = getNumericSize(size as LoadingSize);
    const fadeAnim = useRef(new Animated.Value(0)).current;

    useEffect(() => {
        Animated.timing(fadeAnim, {
            toValue: visible ? 1 : 0,
            duration: 200,
            useNativeDriver: true,
        }).start();
    }, [visible, fadeAnim]);

    const handleMaskPress = useCallback(() => {
        if (maskClosable) {
            onClose?.();
        }
    }, [maskClosable, onClose]);

    // 渲染加载内容
    const renderContent = () => {
        const isVertical = vertical || textPosition === 'bottom';
        const containerStyle = isVertical ? styles.containerVertical : styles.containerHorizontal;
        const textMargin = isVertical 
            ? { marginTop: text ? textGap : 0 } 
            : { marginLeft: text ? textGap : 0 };

        return (
            <View
                style={[
                    styles.container,
                    containerStyle,
                    backgroundColor && { backgroundColor },
                    borderRadius !== undefined && { borderRadius },
                    !fullscreen && style,
                ]}
            >
                <LoadingIndicator
                    type={type}
                    size={numericSize}
                    color={fullscreen ? '#FFFFFF' : color}
                    secondaryColor={secondaryColor}
                    duration={duration}
                >
                    {children}
                </LoadingIndicator>
                {text && (
                    <Text
                        style={[
                            styles.text,
                            fullscreen && styles.textFullscreen,
                            textMargin,
                            textStyle,
                        ]}
                    >
                        {text}
                    </Text>
                )}
            </View>
        );
    };

    // 全屏模式
    if (fullscreen) {
        if (!visible) return null;

        return (
            <Modal
                visible={visible}
                transparent
                animationType="none"
                statusBarTranslucent
                onRequestClose={onClose}
            >
                <TouchableWithoutFeedback onPress={handleMaskPress}>
                    <Animated.View
                        style={[
                            styles.fullscreenOverlay,
                            { backgroundColor: maskColor, opacity: fadeAnim },
                        ]}
                    >
                        <TouchableWithoutFeedback>
                            <View style={[styles.fullscreenContent, contentStyle]}>
                                {renderContent()}
                            </View>
                        </TouchableWithoutFeedback>
                    </Animated.View>
                </TouchableWithoutFeedback>
            </Modal>
        );
    }

    // 内联模式
    if (!visible) return null;
    return renderContent();
};

// ==================== 命令式 API ====================

DbLoading.show = (options) => {
    loadingManager.show(options);
};

DbLoading.hide = () => {
    loadingManager.hide();
};

// ==================== Loading Provider（命令式调用需要） ====================

export const DbLoadingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, setState] = React.useState<LoadingState>({
        visible: false,
    });

    useEffect(() => {
        const unsubscribe = loadingManager.subscribe(setState);
        return () => { unsubscribe(); };
    }, []);

    const handleClose = useCallback(() => {
        loadingManager.hide();
    }, []);

    return (
        <>
            {children}
            <DbLoading
                visible={state.visible}
                fullscreen={state.fullscreen}
                type={state.type}
                size={state.size}
                color={state.color}
                secondaryColor={state.secondaryColor}
                text={state.text}
                textPosition={state.textPosition}
                textStyle={state.textStyle}
                textGap={state.textGap}
                maskColor={state.maskColor}
                maskClosable={state.maskClosable}
                onClose={handleClose}
                contentStyle={state.contentStyle}
                duration={state.duration}
                vertical={state.vertical}
                backgroundColor={state.backgroundColor}
                borderRadius={state.borderRadius}
            >
                {state.children}
            </DbLoading>
        </>
    );
};

DbLoading.displayName = 'DbLoading';

export default DbLoading;
export type {
    DbLoadingProps,
    LoadingMethods,
    LoadingType,
    LoadingSize,
    LoadingIndicatorProps,
} from './DbLoading.types';
